/**
 * Company facts — the single definition of who publishes this site.
 *
 * Reuse: every string here is copied verbatim from the corporate site's footer,
 * `web/omegaalgo.com/index.html` (the `<footer>` block), which is the surface a
 * reader can check against Companies House. Nothing is re-typed at a call site;
 * the site footer, the privacy page and the terms page all read these constants,
 * and `chrome.test.ts` pins the footer to them.
 *
 * Marks: "Arrow Chess" is the product name. The trademark position is AMBER
 * (`docs/legal/arrow-chess-trademark-clearance.md`), so no ® or ™ appears
 * anywhere on this site — an unregistered ® is a criminal offence under s.95
 * Trade Marks Act 1994, and a ™ on a name whose clearance is unfinished is a
 * claim we are not ready to defend. Plain word, plain copyright line.
 */

/** Registered name, exactly as filed. */
export const COMPANY_NAME = "Omega Algorithms Ltd";

/** Companies House registered number. Incorporated 2026-09-01, England and Wales. */
export const COMPANY_NUMBER = "17430310";

/** Registered office, verbatim from web/omegaalgo.com/index.html. */
export const REGISTERED_OFFICE = "71–75 Shelton Street, Covent Garden, London, WC2H 9JQ, United Kingdom";

/** The one published contact address (corporate site footer). */
export const CONTACT_EMAIL = "contact@omegaalgo.com";

/** The corporate site, for the "who we are" link. */
export const COMPANY_URL = "https://omegaalgo.com";

/**
 * The PRODUCT's support address — a different quantity from
 * {@link CONTACT_EMAIL}, which is the company's.
 *
 * Owner decision, 2026-09-01 (`docs/launch/arrowchess-com-site-2026-09.md` §3):
 * `support@arrowchess.com` is the published contact for Arrow Chess and the
 * address behind the `#support` anchor that the App Store support URL points
 * at. Deliverable receive-side on day one via Cloudflare Email Routing (§6 row
 * 8); do not substitute the corporate address, which goes to a different
 * mailbox.
 */
export const SUPPORT_EMAIL = "support@arrowchess.com";

/**
 * The privacy contact the Privacy Policy publishes (`legal/privacy-policy.md`,
 * the canonical copy). Repeated here only so a page can print it;
 * `legal.test.ts` fails if the policy stops naming this address. Account-safety
 * copy (the e-mail-change cancel page, #3549) points here, not at the old
 * `privacy@chessstream.live`.
 */
export const PRIVACY_EMAIL = "privacy@arrowchess.com";

/** The product this site is about. */
export const PRODUCT_NAME = "Arrow Chess";

/**
 * The year the copyright line asserts.
 *
 * Fixed, not `new Date().getFullYear()`: a clock-derived year makes the rendered
 * page depend on when it is viewed rather than on when it was published, which
 * defeats both the screenshot tests and the point of the notice. Bump it with
 * the next substantive publication.
 */
export const COPYRIGHT_YEAR = 2026;

/** `© 2026 Omega Algorithms Ltd. All rights reserved.` */
export const copyrightLine = (): string => `© ${COPYRIGHT_YEAR} ${COMPANY_NAME}. All rights reserved.`;

/** `Arrow Chess is a product of Omega Algorithms Ltd.` */
export const productLine = (): string => `${PRODUCT_NAME} is a product of ${COMPANY_NAME}.`;

/**
 * The registration sentence, wording mirrored from the corporate footer:
 * "…is a private company limited by shares, registered in England and Wales
 * under company number 17430310. Registered office: …".
 */
/**
 * The three documents every page links to, in the order the footer shows them.
 * One list, so a route added here and a route registered in `src/routes.ts`
 * are checked against each other by `routes.test.ts`.
 */
export const FOOTER_LEGAL_LINKS: { href: string; label: string }[] = [
  { href: "/privacy", label: "Privacy" },
  { href: "/terms", label: "Terms" },
  { href: "/credits", label: "Credits" },
];

/**
 * The paragraphs of the footer's legal strip, as text.
 *
 * A function returning strings rather than elements, so the footer's legal
 * content is checkable in vitest's node environment — the whole of this site is
 * tested without a DOM, and a jsdom dependency to assert two sentences would be
 * a poor trade.
 */
export const footerLegalLines = (): string[] => [copyrightLine(), `${productLine()} ${registrationLine()}`];

export const registrationLine = (): string =>
  `${COMPANY_NAME} is a private company limited by shares, registered in England and Wales under company number ` +
  `${COMPANY_NUMBER}. Registered office: ${REGISTERED_OFFICE}.`;
